import { useNavigate } from "react-router-dom";
import { PaperButton, AudioSettings } from "@/components/paper";
import { audio } from "@/lib/audio";
import { Palette, Users, Globe, LogIn, ArrowRight } from "lucide-react";

const FLOW = ["PROMPT", "DRAW", "INSPECT", "VOTE", "DEFEND", "ELIMINATE", "REVEAL"];

export default function Home() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen">
      <TopNav nav={nav} />

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 pt-10 pb-16">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-block font-hand font-bold text-sm px-3 py-1 mb-4 sticky-note bg-sticky-yellow -rotate-2">a LOSTDC party game</div>
            <h1 className="font-hand font-bold leading-[0.9] tracking-tight text-6xl sm:text-7xl">LOSTDC</h1>
            <h2 className="font-hand font-bold text-4xl sm:text-5xl mt-1" style={{ WebkitTextStroke: "1px #1C1B1A", color: "#FFDD2D" }}>IMPOSTER DRAW</h2>
            <p className="font-script text-3xl sm:text-4xl mt-4 text-ink2">Draw. Bluff. Defend.</p>
            <p className="mt-5 text-lg max-w-xl text-ink2 font-semibold">
              Everyone gets a secret drawing prompt. One or more players get a <span className="highlight-mark">different</span> prompt.
              Draw fast, inspect the sketches, find the suspicious one, defend yourself, and eliminate the imposters.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <PaperButton data-testid="home-create-room" sound="createRoom" color="#C7EFC2" className="text-xl px-7 py-4" onClick={() => nav("/play?mode=create")}>
                <span className="inline-flex items-center gap-2"><Palette strokeWidth={2.5} /> CREATE ROOM</span>
              </PaperButton>
              <PaperButton data-testid="home-join-room" sound="joinRoom" color="#BAE3FB" className="text-xl px-7 py-4" onClick={() => nav("/play?mode=join")}>
                <span className="inline-flex items-center gap-2"><LogIn strokeWidth={2.5} /> JOIN ROOM</span>
              </PaperButton>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="card-paper p-6 rotate-1 relative">
              <div className="tape" style={{ top: -13, left: "35%", transform: "rotate(-5deg)" }} />
              <div className="font-hand font-bold text-xl mb-3">How to play</div>
              <ol className="space-y-2 font-semibold text-ink2">
                {["Pick a username + avatar (no login!)", "Create a room, share the code with friends", "Everyone draws their secret prompt", "Vote on the most suspicious drawing", "Put it in the 🔥 Hot Seat & defend", "Eliminate, reveal roles, score points!"].map((t, i) => (
                  <li key={i} className="flex gap-2"><span className="font-hand font-bold text-marker-blue">{i + 1}.</span> {t}</li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* FLOW */}
        <div className="mt-14">
          <div className="font-hand font-bold text-2xl mb-4 text-center">The Round</div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {FLOW.map((step, i) => (
              <div key={step} className="flex items-center gap-2 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <span className="sticky-note bg-cream font-hand font-bold px-3 py-1.5" style={{ transform: `rotate(${(i % 2 ? 1 : -1) * 2}deg)` }}>{step}</span>
                {i < FLOW.length - 1 && <ArrowRight size={18} strokeWidth={3} className="text-ink2" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-6xl mx-auto px-5 pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: <Users strokeWidth={2.5} />, t: "Play with friends", d: "3–12 players in a private room. Bring your own voice chat.", c: "#FFE9A8" },
            { icon: <Globe strokeWidth={2.5} />, t: "Works in browser", d: "No installs. Phone, tablet or desktop — all welcome.", c: "#BAE3FB" },
            { icon: <LogIn strokeWidth={2.5} />, t: "No login required", d: "Just a username and an avatar. Jump straight in.", c: "#FAC7D9" },
            { icon: <Palette strokeWidth={2.5} />, t: "Fast & fun", d: "Quick rounds, dramatic reveals, one-more-round energy.", c: "#C7EFC2" },
          ].map((f, i) => (
            <div key={i} className="card-paper p-5" style={{ background: f.c, transform: `rotate(${(i % 2 ? 1 : -1)}deg)` }}>
              <div className="mb-2">{f.icon}</div>
              <div className="font-hand font-bold text-xl">{f.t}</div>
              <div className="font-semibold text-ink2 mt-1">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t-2 border-ink/20 py-6 text-center font-semibold text-ink2">
        <span className="font-hand font-bold">LOSTDC</span> · Imposter Draw · Draw. Bluff. Defend.
      </footer>
    </div>
  );
}

export function TopNav({ nav }) {
  return (
    <nav className="sticky top-0 z-40 border-b-2 border-ink/20" style={{ background: "#F4EBD0" }}>
      <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
        <button data-testid="nav-home" onClick={() => { audio.play("click"); nav("/"); }} className="font-hand font-bold text-2xl">LOSTDC</button>
        <div className="flex items-center gap-2 sm:gap-4">
          <button data-testid="nav-play" onClick={() => { audio.play("click"); nav("/play?mode=create"); }} className="font-hand font-bold hover:text-marker-blue">PLAY</button>
          <AudioSettings />
        </div>
      </div>
    </nav>
  );
}